import cn from 'classnames';
import React, { useEffect, useId, useRef, useState } from 'react';
import Text from '../custom-text/custom-text.jsx';
import styles from './custom-text-input-field.module.scss';

export default function CustomTextInputField({
  label = '',
  value = '',
  required = false,
  placeholder = '',
  validations = [],
  defaultValidationError = 'Please complete this field.',
  testid = '',
  onChange,
  disabled = false,
  hideLabel = false,
  endAdornment = null,
  id,
  fullWidth = false,
  className = '',
  helperText = '',
  error = false,
  ...props
}) {
  const [isPristine, setIsPristine] = useState(true);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  const inputId = id || useId();

  const getValidationResult = () => {
    if (required && !value) {
      return { valid: false, error: defaultValidationError };
    }
    if (validations?.length) {
      for (const check of validations) {
        if (typeof check.validation === 'function' && !check.validation(value)) {
          return { valid: false, error: check.validationError };
        }
      }
    }
    return { valid: true, error: '' };
  };

  const { valid, error: validationError } = getValidationResult();

  const handleChange = e => {
    if (disabled) return;
    onChange(e);
  };

  useEffect(() => {
    if (inputRef.current && required) {
      inputRef.current.onfocus = () => {
        if (isPristine) {
          setFocused(true);
          setIsPristine(false);
        }
      };

      inputRef.current.onblur = () => {
        setFocused(false);
      };
    }
  }, []);

  const invalidInput = required && !isPristine && !valid && !focused;

  return (
    <div className={cn(styles.inputWrapper, fullWidth && styles.fullWidth)}>
      {label && !hideLabel && (
        <div className={styles.labelContainer}>
          <Text as="label" variant="l2" color="#324975" htmlFor={inputId}>
            {label}
            {required && <span className={styles.srOnly}> (required)</span>}
          </Text>
          {!required && (
            <Text as="span" variant="l2" color="#6B7280">
              Optional
            </Text>
          )}
        </div>
      )}
      <div
        className={cn(
          styles.inputContainer,
          (invalidInput || error) && styles.error,
          disabled && styles.disabled
        )}
        aria-invalid={invalidInput || error} 
      >
        <input
          id={inputId}
          type="text"
          className={cn(
            styles.input,
            className,
            endAdornment && styles.hasEndAdornment
          )}
          onChange={handleChange}
          value={value}
          placeholder={placeholder}
          ref={inputRef}
          data-testid={testid}
          disabled={disabled}
          required={required}
          aria-required={required}
          aria-describedby={
            helperText || invalidInput || error 
              ? `${inputId}-description`
              : undefined
          }
          {...props}
        />
        {endAdornment && (
          <div className={styles.endAdornment}>{endAdornment}</div>
        )}
      </div>
      {(helperText || invalidInput || error) && (
        <div id={`${inputId}-description`} className={styles.helperText}>
          <Text
            as="span"
            variant="b2"
            color={(invalidInput || error) ? '#DC2626' : '#6B7280'}
          >
            {error ? helperText : invalidInput ? validationError : helperText}
          </Text>
        </div>
      )}
    </div>
  );
}
